import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { AuthenticationService, IWebAppUser } from '@services/authentication.service'
import { Account, AccountsService, IAccount, IAccountGroup, IAccountGroupUser, IAccountUser } from '@services/accounts.service';
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged, finalize, filter } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { CommentDialogComponent } from 'src/app/dialogs/comment-dialog/comment-dialog.component';

/**
 * AccountManageOrganisationGroupsComponent
 */
@Component({
	selector: 'app-account-manage-groups',
	templateUrl: './account-manage-groups.component.html',
	styleUrls: ['./account-manage-groups.component.scss']
})
export class AccountManageOrganisationGroupsComponent implements OnChanges, OnInit, OnDestroy {

	/** Account GUID */
	@Input() accountGUID: string;
	/** System Users List */
	public accountUsers: IAccountUser[];
	/** My Accounts List */
	public myAccounts: IAccount[];
	/** Selected Account */
	public selectedAccount: IAccount;
	/** Current User */
	public thisUser: IWebAppUser;
	/** Observable Subject Finalise */
	private finalise = new Subject<void>();
	/** Account Groups */
	public accountGroups: IAccountGroup[]
	/** Account System Groups */
	public accountSystemGroups: IAccountGroup[]
	/** Selected Account Group */
	public selectedGroup: IAccountGroup | undefined
	/** Account Group Users List */
	public accountGroupUsers: IAccountGroupUser[]
	/** validationRules */
	public validationRules = { groupName: { required: true } }

	/**
	 * Default Constructor
	 * @param authenticationService authenticationService
	 * @param accountsService accountsService
	 * @param globalFunctions globalFunctions
	 * @param siteSubscriptions siteSubscriptions
	 * @param organisationDataService organisationDataService
	 * @param dialog dialog
	 */
	constructor(
		private authenticationService: AuthenticationService,
		private accountsService: AccountsService,
		private globalFunctions: GlobalFunctions,
		private siteSubscriptions: siteSubscriptions,
		public dialog: MatDialog
	) {
		// Validate the user
		this.authenticationService.currentUser$.pipe(
			takeUntil(this.finalise),
			distinctUntilChanged()
		).subscribe({
			next: (currentUser) => {
				// Check we have a validated User
				if (currentUser && !this.thisUser) { this.thisUser = <IWebAppUser>currentUser }
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.authenticationService.currentUser$", false) },
			//complete: () => { console.log("this.authenticationService.currentUser$: Complete"); }
		})
	}

	/**
	 * Subscribes to changes in the input values and updates the selected asset object
	 * @param changes
	 */
	ngOnChanges(changes: SimpleChanges) {
		if (this.accountGUID != '') {
			this.accountGroups = [];
			this.accountSystemGroups = [];
			this.accountGroupUsers = [];
			this.selectedGroup = undefined;
			this.subscribeAccounts(this.accountGUID);
		}
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void { }

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Subscribe to the list of My accounts
	 */
	subscribeAccounts(accountGUID: String) {

		this.siteSubscriptions.myAccounts$.pipe(
			takeUntil(this.finalise),
			distinctUntilChanged()
		).subscribe({
			next: (myAccounts) => {
				if (myAccounts) {
					this.myAccounts = myAccounts;
					this.myAccounts = this.siteSubscriptions.normalizeFeatures(this.myAccounts) as Account[];
					this.selectedAccount = <IAccount>this.myAccounts.find(({ GUID }) => GUID.toUpperCase() === accountGUID.toUpperCase())

					// Check we have an account
					if (this.selectedAccount) {

						//this.selectedAccount.Admin = true;
						this.accountGUID = this.selectedAccount.GUID;

						// Get Users for the account
						this.getAccountGroups(this.accountGUID);
					}
				}
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.siteSubscriptions.myAccounts$", false) },
			//complete: () => { console.log("this.siteSubscriptions.myAccounts$: Complete"); }
		})
	}

	/**
	 * Get Account Groups
	 * @param accountGUID
	 */
	getAccountGroups(accountGUID: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.accountGroups = [];
		this.accountSystemGroups = [];

		// Get Groups for the account
		this.accountsService.getAccountGroups$(accountGUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((groups) => !!groups),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (groups: IAccountGroup[]) => {
				try {
					// let groups = <IAccountGroup[]>response
					this.accountGroups = <IAccountGroup[]>groups.filter(({ SystemGroup }) => SystemGroup == false)
					this.accountSystemGroups = <IAccountGroup[]>groups.filter(({ SystemGroup }) => SystemGroup == true)
				} catch (error) { this.globalFunctions.HandleError(error, "") }
			},
			error: (error: HttpErrorResponse) => {
				if (error.status != 404) {
					this.accountGroupUsers = []
					this.selectedGroup = undefined
					console.error(error)
					this.globalFunctions.HandleError(error, "this.accountsService.getAccountGroups$: Error retrieving Account Groups")
				} else { console.error(error.error); }
			},
			//complete: () => { console.log("this.accountsService.getAccountGroups$: Complete"); }
		});
	}

	/**
	 * SelectGroup
	 * @param group
	 */
	selectGroup(group: IAccountGroup) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.selectedGroup = group;
		this.accountGroupUsers = []
		// Get Groups for the account
		this.accountsService.getAccountGroupUsers$(this.accountGUID, group.AccountGroupID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response) => {
				try {
					this.accountGroupUsers = <IAccountGroupUser[]>response
				} catch (error) { this.globalFunctions.HandleError(error, "") }
			},
			error: (error: HttpErrorResponse) => {

				if (error.status != 404) {
					console.error(error)
					this.globalFunctions.HandleError(error, "this.accountsService.getAccountGroupUsers$: Error retrieving Account Group Users")
				} else { console.error(error.error); }
			},
			//complete: () => { console.log("this.accountsService.getAccountGroupUsers$: Complete"); }
		});
	}

	/**
	 * updateGroupUsers
	 * @param ID
	 */
	updateGroupUsers(ID: number) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		let groupID: number = this.selectedGroup?.AccountGroupID || 0

		// Get Groups for the account
		this.accountsService.patchAccountGroupUsers$(this.accountGUID, groupID, this.accountGroupUsers).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response) => {
				try {
					this.accountGroupUsers = <IAccountGroupUser[]>response;
					this.getAccountUsers(this.accountGUID);

				} catch (error) { this.globalFunctions.HandleError(error, "") }
			},
			error: (error: HttpErrorResponse) => {
				if (error.status != 404) {
					console.error(error)
					this.globalFunctions.HandleError(error, "this.accountsService.patchAccountGroupUsers$: Error patching Account Group Users")
				} else { console.error(error.error); }
			},
			//complete: () => { console.log("this.accountsService.patchAccountGroupUsers$: Complete"); }
		});

	}


	/**
	 * Get List of Account Users
	 * @param accountGUID
	 */
	getAccountUsers(accountGUID: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.accountUsers = []
		// Get Users for the account
		this.accountsService.getAccountUsers$(this.accountGUID, { "systemAccount": accountGUID, "showDisabled": true }).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((users) => !!users),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (users) => {
				try {
					this.siteSubscriptions.setAccountUsers(<IAccountUser[]>users)
				} catch (error) { this.globalFunctions.HandleError(error, "") }
			},
			error: (error: HttpErrorResponse) => {

				if (error.status != 404) {
					console.error(error)
					this.globalFunctions.HandleError(error, "this.accountsService.getAccountUsers$: Error retrieving Invitations")
				} else {
					console.error(error.error);
				}
			},
			//complete: () => { console.log("this.accountsService.getAccountUsers$: Complete"); }
		});
	}

	/**
	 * deleteGroup
	 * @param group
	 */
	deleteGroup(group: IAccountGroup) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.globalFunctions.UserConfirm$("Delete Group: " + group.Name + "?  Note: All Group users will be reset and any Hierarchy permissions will be removed",
			undefined, undefined, undefined, "50%")
			.subscribe(async OKResult => {
				if (OKResult) {
					// Get Groups for the account
					this.accountsService.deleteAccountGroup$(this.accountGUID, group.AccountGroupID).pipe(
						takeUntil(fetchDataSubject), distinctUntilChanged(),
						filter((response) => !!response),
						finalize(() => {
							// Ensure the subscription is terminated and the subject is completed
							fetchDataSubject.next();
							fetchDataSubject.complete();
						})
					).subscribe({
						next: (response) => {
							try {
								// Get Users for the account
								this.getAccountGroups(this.accountGUID);
								this.selectedGroup = undefined;
								this.getAccountUsers(this.accountGUID);
							} catch (error) { this.globalFunctions.HandleError(error, "") }
						},
						error: (error: HttpErrorResponse) => {

							if (error.status != 404) {
								console.error(error)
								this.globalFunctions.HandleError(error, "this.accountsService.deleteAccountGroup$: Error deleting Account Group")
							} else {
								console.error(error.error);
							}
						},
						//complete: () => { console.log("this.accountsService.deleteAccountGroup$: Complete"); }
					});
				}
			});
	}

	/**
	 * Add a new Group
	 * @param event
	 */
	onAddGroup(event: Event) {

		const dialogRef = this.dialog.open(CommentDialogComponent, {
			width: '500px',
			data: {
				"HeaderText": "New Group",
				"DialogText": "Enter the new group name?",
				"DialogPrompt": "Group Name"
			}
		});

		dialogRef.afterClosed().pipe(filter(result => !!result)
		).subscribe(result => { this.CreateGroup(result.comment) });

	}


	/**
	 * Logic to determine if the menu should stay open
	 * @returns
	 */
	shouldKeepMenuOpen(): boolean {
		// Your logic here
		return true;
	}

	/**
	 * Add New Group
	 * @param args
	 */
	CreateGroup(name: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// this.closeMenu();
		let newGroup = <IAccountGroup>{
			/** Account ID */
			AccountID: this.selectedAccount.ID,
			/** Group Name */
			Name: name
		}

		// Patch the Selected Group
		this.accountsService.postAccountGroup$(this.accountGUID, newGroup).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response) => {
				try {
					// Get Users for the account
					this.getAccountGroups(this.accountGUID);
					this.selectedGroup = <IAccountGroup>response
				} catch (error) { this.globalFunctions.HandleError(error, "") }
			},
			error: (error: HttpErrorResponse) => {
				if (error.status != 404) {
					console.error(error)
					this.globalFunctions.HandleError(error, "this.accountsService.postAccountGroup$: Error Adding Account Group")
				} else { console.error(error.error); }
			},
			//complete: () => { console.log("this.accountsService.postAccountGroup$: Complete"); }
		});
	}

	/**
	 * update Group Name
	 * @param args
	 */
	updateGroupName(group: IAccountGroup) {

		let data = { ...group };
		const dialogRef = this.dialog.open(CommentDialogComponent, {
			width: '500px',
			data: {
				"HeaderText": "Edit Group",
				"DialogText": "Enter the new group name?",
				"DialogPrompt": "Group Name"
			}
		});

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			data.Name = result.comment
			this.patchGroup(data)
		});

	}

	/**
	 * patch Account Group
	 * @param group
	 */
	patchGroup(group: IAccountGroup) {

		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Patch the Selected Group
		this.accountsService.patchAccountGroup$(this.accountGUID, group.AccountGroupID, group).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response) => {
				try {
					// Get Users for the account
					this.getAccountGroups(this.accountGUID);
					this.selectedGroup = <IAccountGroup>response

				} catch (error) { this.globalFunctions.HandleError(error, "") }
			},
			error: (error: HttpErrorResponse) => {

				if (error.status != 404) {
					console.error(error)
					this.globalFunctions.HandleError(error, "this.accountsService.patchAccountGroup$: Error patching Account Group")
				} else {
					console.error(error.error);
				}
			},
			//complete: () => { console.log("this.accountsService.patchAccountGroup$: Complete"); }
		});

	}
}
